import { Response, apiAbortController } from '@ui/src';
import api from '../../../../api/api';
import { Balance, CustomerProps } from '../../../common/types';
import {
  BaseInfoFormValues,
  Device,
  Lock,
  LockFormValues,
  LockAuditLog,
  DeviceSession,
} from '../../types';

export const individualCustomersApi = api.injectEndpoints({
  endpoints: (build) => ({
    getIndividualUsers: build.query<Response<CustomerProps>, string>({
      query: (query) => ({
        url: `/v1/clients-organizations${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualUser: build.query<{ data: CustomerProps }, string>({
      query: (id) => ({
        url: `/v1/clients-organizations/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),

    getIndividualAccountBalance: build.query<Response<Balance>, string>({
      query: (id) => ({
        url: `/v1/accounts/${id}/balances?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),

    updateIndividualUserBaseInformation: build.mutation<
      {
        data: CustomerProps;
      },
      { body: BaseInfoFormValues; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),

    convertCofToIndividual: build.mutation<{ data: CustomerProps }, string>({
      query: (id) => ({
        url: `/v1/clients-organizations/${id}/convert-to-individual`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getMainChannel: build.query<Response<{ id: string }>, string>({
      query: (id) => ({
        url: `/v1/channels?organizationId=${id}&channelType=MAIN`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),

    getCustomerDevices: build.query<
      Response<Device>,
      { id: string; query: string }
    >({
      query: ({ id, query }) => ({
        url: `/v1/clients-organizations/${id}/devices${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerActiveLocks: build.query<Response<Lock>, string>({
      query: (id) => ({
        url: `/v1/locks?take=1&locked=true&userId=${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerLocks: build.query<Response<Lock>, string>({
      query: (query) => ({
        url: `/v1/locks${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerLockItem: build.query<{ data: Lock }, string>({
      query: (id) => ({
        url: `/v1/locks/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCustomerLock: build.mutation<{ data: Lock }, LockFormValues>({
      query: (body) => ({
        url: '/v1/locks',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),

    updateCustomerLock: build.mutation<
      { data: Lock },
      { body: LockFormValues; id: string }
    >({
      query: (data) => ({
        url: `/v1/locks/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerLocksAuditLogs: build.query<Response<LockAuditLog>, string>({
      query: (query) => ({
        url: `/v1/idm-audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerLockItemAuditLogs: build.query<Response<LockAuditLog>, string>({
      query: (query) => ({
        url: `/v1/idm-audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerLocksAuditLogItem: build.query<{ data: LockAuditLog }, string>({
      query: (id) => ({
        url: `/v1/idm-audit-log/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    endCustomerSession: build.mutation<
      { data: { count: number } },
      {
        id: string;
        deviceId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/devices/${data.deviceId}/sessions`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerDeviceSessions: build.query<
      Response<DeviceSession>,
      { id: string; entityId: string; query: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/device/${data.entityId}/sessions${data.query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useGetIndividualUsersQuery,
  useLazyGetIndividualUsersQuery,
  useLazyGetIndividualUserQuery,
  useUpdateIndividualUserBaseInformationMutation,
  useLazyGetIndividualAccountBalanceQuery,
  useConvertCofToIndividualMutation,
  useLazyGetMainChannelQuery,
  useLazyGetCustomerDevicesQuery,
  useLazyGetCustomerLockItemAuditLogsQuery,
  useLazyGetCustomerLockItemQuery,
  useLazyGetCustomerLocksAuditLogItemQuery,
  useLazyGetCustomerLocksAuditLogsQuery,
  useLazyGetCustomerLocksQuery,
  useCreateCustomerLockMutation,
  useUpdateCustomerLockMutation,
  useEndCustomerSessionMutation,
  useLazyGetCustomerActiveLocksQuery,
  useLazyGetCustomerDeviceSessionsQuery,
} = individualCustomersApi;

export const {
  endpoints: {
    getIndividualUsers,
    getIndividualUser,
    updateIndividualUserBaseInformation,
    getIndividualAccountBalance,
    convertCofToIndividual,
    getMainChannel,
    getCustomerDevices,
    getCustomerLockItem,
    getCustomerLockItemAuditLogs,
    getCustomerLocksAuditLogItem,
    getCustomerLocks,
    getCustomerLocksAuditLogs,
    createCustomerLock,
    updateCustomerLock,
    endCustomerSession,
    getCustomerActiveLocks,
    getCustomerDeviceSessions,
  },
} = individualCustomersApi;
